// MyTimeLog.js
import React, { useEffect, useMemo, useState, Fragment } from "react";
import API from "./api";
import { Building2, Home, Globe } from "lucide-react";

// ---------- Helpers ----------
function kmToMiles(km, decimals = 2) {
  const n = Number(km);
  if (!isFinite(n)) return null;
  return Number((n * 0.621371).toFixed(decimals));
}

function fmtHMS(dt) {
  if (!dt) return "-";
  const m = String(dt).match(/(\d{2}):(\d{2})(?::(\d{2}))?/);
  if (!m) return "-";
  return `${m[1]}:${m[2]}`;
}

function hmFromDateTime(dt) {
  if (!dt) return "";
  const t = String(dt).split(" ")[1] || dt;
  const [hh, mm] = t.split(":");
  return hh && mm ? `${hh}:${mm}` : "";
}

function combineDateHM(dateStr, hm) {
  if (!hm) return null;
  return `${dateStr} ${hm}:00`;
}

function fmtTimeRange(scheduled_at, objective_event_end_time) {
  if (!scheduled_at) return "";
  const start = (scheduled_at.split(" ")[1] || "").slice(0, 5);
  if (!objective_event_end_time) return start;
  const end = objective_event_end_time.slice(0, 5);
  return `${start}-${end}`;
}

function toDateObj(str) {
  const d = new Date(str);
  return isNaN(d) ? null : d;
}

function diffMin(a, b) {
  const da = a ? toDateObj(a) : null;
  const db = b ? toDateObj(b) : null;
  if (!da || !db) return null;
  return Math.round((da - db) / 60000);
}

function deltaBadge(mins) {
  if (mins === null) return <span className="text-gray-400">n/a</span>;
  const abs = Math.abs(mins);
  const color = abs <= 2 ? "text-green-600" : abs <= 10 ? "text-yellow-600" : "text-red-600";
  const sign = mins > 0 ? "+" : "";
  return (
    <span className={`font-semibold ${color}`}>
      {sign}
      {mins} min
    </span>
  );
}

function isSpecialCanceled(visit) {
  const notes = (visit.notes || "").toString();
  return notes.includes("NoShow/withAppointmentConfirmation") || notes.includes("Canceled by BBarent");
}

function isTelehealthVisit(v) {
  return String(v.delivery_mode || "").toLowerCase() === "telehealth";
}

// ---------- Discipline & Event badges ----------
function getDisciplineBadgeConfig(serviceType) {
  switch (serviceType) {
    case "EI": return { label: "EI", bg: "bg-blue-100", border: "border-blue-200", text: "text-blue-800" };
    case "PI": return { label: "PI", bg: "bg-orange-100", border: "border-orange-200", text: "text-orange-800" };
    case "ABA": return { label: "ABA", bg: "bg-purple-100", border: "border-purple-200", text: "text-purple-800" };
    case "OT": return { label: "OT", bg: "bg-teal-100", border: "border-teal-200", text: "text-teal-800" };
    case "PT": return { label: "PT", bg: "bg-emerald-100", border: "border-emerald-200", text: "text-emerald-800" };
    case "ST": return { label: "ST", bg: "bg-green-100", border: "border-green-200", text: "text-green-800" };
    default: return null;
  }
}

function DisciplineBadge({ serviceType }) {
  const cfg = getDisciplineBadgeConfig(serviceType);
  if (!cfg) return null;
  return (
    <span className={["inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold", "border", cfg.bg, cfg.border, cfg.text].join(" ")}>
      {cfg.label}
    </span>
  );
}

function EventBadge() {
  return (
    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold bg-yellow-50 border border-yellow-200 text-yellow-700">
      Event
    </span>
  );
}

function TelehealthBadge() {
  return (
    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold bg-sky-50 border border-sky-200 text-sky-700">
      Telehealth
    </span>
  );
}

/** -----------------------------------------
 * Work helpers
 * ----------------------------------------- */
function isWorkItem(x) {
  return x && (x.__kind === "work" || x._is_work === true);
}

function parseHHMM(val) {
  const s = String(val || "").trim();
  const m = s.match(/(\d{2}):(\d{2})/);
  return m ? `${m[1]}:${m[2]}` : "";
}

function normalizeTeacherWorkToUnified(rows) {
  const list = Array.isArray(rows) ? rows : [];
  return list.map((tw) => {
    const from = tw.from_hhmm || parseHHMM(tw.scheduled_at) || "";
    const to = tw.to_hhmm || tw.end_hhmm || "";
    return {
      ...tw,
      __kind: "work",
      _is_work: true,
      scheduled_time: from,
      event_end_time: to ? `${String(to).slice(0, 5)}:00` : null,
      work_id: tw.id,
      canceled_reason: tw.canceled_reason || null,
      work_time_log: tw.work_time_log || null,
    };
  });
}

function pickNumber(...xs) {
  for (const x of xs) {
    const n = x != null ? Number(x) : null;
    if (n != null && Number.isFinite(n)) return n;
  }
  return null;
}

function getWorkRoutePoint(w) {
  if (String(w.location_mode || "") === "remote") return null;
  if (String(w.status || "") === "canceled") return null;

  if (String(w.location_mode || "") === "org_location") {
    const lat = pickNumber(w.org_location_latitude, w.org_location_lat, w.org_lat, w.latitude);
    const lon = pickNumber(w.org_location_longitude, w.org_location_lon, w.org_lon, w.longitude);
    if (lat != null && lon != null) {
      return { lat, lon, label: w.org_location_name ? String(w.org_location_name) : "Org location" };
    }
    return null;
  }

  if (String(w.location_mode || "") === "teacher_home") {
    const lat = pickNumber(w.teacher_lat, w.teacher_home_lat, w.home_lat);
    const lon = pickNumber(w.teacher_lon, w.teacher_home_lon, w.home_lon);
    if (lat != null && lon != null) return { lat, lon, label: "Teacher home" };
    return null;
  }

  return null;
}

function getVisitRoutePoint(v) {
  if (isTelehealthVisit(v)) return null;
  const isCanceled = String(v.status || "") === "canceled";
  if (isCanceled && !isSpecialCanceled(v)) return null;

  const atClinic = String(v.location_mode || "").toLowerCase() === "at_clinic";

  // clinic first (if at_clinic)
  if (atClinic) {
    const lat = pickNumber(v.clinic_lat, v.org_location_lat, v.visit_org_lat, v.dest_lat);
    const lon = pickNumber(v.clinic_lon, v.org_location_lon, v.visit_org_lon, v.dest_lon);
    if (lat != null && lon != null) {
      return { lat, lon, label: v.clinic_name || v.org_location_name || "Clinic" };
    }
    // fallback: no coords -> null
    return null;
  }

  // at_home -> student coords
  const lat = pickNumber(v.dest_lat, v.student_lat, v.latitude, v.checkin_lat);
  const lon = pickNumber(v.dest_lon, v.student_lon, v.longitude, v.checkin_lon);

  if (lat != null && lon != null) return { lat, lon, label: "Home" };
  return null;
}


function fmtWorkRange(w) {
  const from = String(w.from_hhmm || w.scheduled_time || "").slice(0, 5);
  const to = String(w.to_hhmm || w.end_hhmm || w.event_end_time || "").slice(0, 5);
  return from && to ? `${from}-${to}` : from || "";
}

function workFromLabel(w) {
  const wt = String(w.work_type || "other");
  const loc =
    w.location_mode === "org_location"
      ? (w.org_location_name || "Org")
      : w.location_mode === "teacher_home"
      ? "Home"
      : "Remote";
  return `work (${wt} @ ${loc})`;
}

export default function MyTimeLog() {
  const [visits, setVisits] = useState([]);
  const [workBlocks, setWorkBlocks] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [changed, setChanged] = useState({});
  const [changedFields, setChangedFields] = useState({});
  const [serverDate, setServerDate] = useState(null);

  useEffect(() => {
    API.get("/server-time")
      .then((res) => {
        const d = (res.data.serverDate || res.data.serverTime || "").slice(0, 10);
        setServerDate(d || new Date().toISOString().slice(0, 10));
      })
      .catch(() => setServerDate(new Date().toISOString().slice(0, 10)));
  }, []);

  useEffect(() => {
    if (!serverDate) return;

    setLoading(true);

    const load = async () => {
      try {
        const res = await API.get("/time-log/my", { params: { date: serverDate } });
        setVisits(res.data.visits || []);
      } catch {
        setVisits([]);
      }

      try {
        const wRes = await API.get("/teacher-work/range", {
          params: { date_from: serverDate, date_to: serverDate },
        });
        setWorkBlocks(normalizeTeacherWorkToUnified(wRes.data || []));
      } catch {
        setWorkBlocks([]);
      }

      setChanged({});
      setChangedFields({});
      setLoading(false);
    };

    load();
  }, [serverDate]);

  const markChanged = (key, field) => {
    setChanged((c) => ({ ...c, [key]: true }));
    setChangedFields((cf) => ({
      ...cf,
      [key]: { ...(cf[key] || {}), [field]: true },
    }));
  };

  const updateVisitTLField = (visitId, field, value) => {
    setVisits((vs) =>
      vs.map((v) => (v.id === visitId ? { ...v, time_log: { ...(v.time_log || {}), [field]: value } } : v))
    );
    markChanged(`v_${visitId}`, field);
  };

  const updateWorkTLField = (workId, field, value) => {
    setWorkBlocks((ws) =>
      ws.map((w) =>
        Number(w.work_id || w.id) === Number(workId)
          ? { ...w, work_time_log: { ...(w.work_time_log || {}), [field]: value } }
          : w
      )
    );
    markChanged(`w_${workId}`, field);
  };

  const updateVisitTimeHM = (visit, field, hm) => {
    const datePart = String(visit.scheduled_at).slice(0, 10);
    const dt = combineDateHM(datePart, hm);
    updateVisitTLField(visit.id, field, dt);
  };

  const updateWorkTimeHM = (work, field, hm) => {
    const datePart = String(work.scheduled_at).slice(0, 10);
    const dt = combineDateHM(datePart, hm);
    const workId = Number(work.work_id || work.id);
    updateWorkTLField(workId, field, dt);
  };

  const unified = useMemo(() => {
    const items = [
      ...(visits || []).map((v) => ({ ...v, __kind: "visit" })),
      ...(workBlocks || []),
    ];
    items.sort((a, b) => String(a.scheduled_at).localeCompare(String(b.scheduled_at)));
    return items;
  }, [visits, workBlocks]);

  const itemIndexMap = useMemo(() => {
    const m = {};
    unified.forEach((it, idx) => {
      if (isWorkItem(it)) m[`w_${Number(it.work_id || it.id)}`] = idx;
      else m[`v_${it.id}`] = idx;
    });
    return m;
  }, [unified]);

  const getItemRoutePoint = (it) => {
    if (isWorkItem(it)) return getWorkRoutePoint(it);
    return getVisitRoutePoint(it);
  };

  const findPrevPhysicalForKey = (key) => {
    const idx = itemIndexMap[key];
    if (idx == null) return null;

    for (let j = idx - 1; j >= 0; j--) {
      const cand = unified[j];

      if (isWorkItem(cand)) {
        if (String(cand.status || "") === "canceled") continue;
        if (String(cand.location_mode || "") === "remote") continue;
      } else {
        const p = getVisitRoutePoint(cand);
        if (!p) continue;
      }

      const p2 = getItemRoutePoint(cand);
      if (p2) return { kind: isWorkItem(cand) ? "work" : "visit", item: cand, point: p2 };
    }

    return null;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // save visits
      for (const v of (visits || [])) {
        const key = `v_${v.id}`;
        if (!changed[key]) continue;

        const tl = v.time_log || {};
        const telehealth = isTelehealthVisit(v);

        const prevPhysical = findPrevPhysicalForKey(key);
        const cf = changedFields[key] || {};
        const body = {};

        if (cf.subjective_in_time) body.subjective_in_time = tl.subjective_in_time || null;
        if (cf.subjective_out_time) body.subjective_out_time = tl.subjective_out_time || null;

        if (cf.event_start_time) body.event_start_time = tl.event_start_time || null;
        if (cf.event_end_time) body.event_end_time = tl.event_end_time || null;

        if (cf.drive_start_time) body.drive_start_time = tl.drive_start_time || null;
        if (cf.drive_end_time) body.drive_end_time = tl.drive_end_time || null;

        if (cf.miles_driven) body.miles_driven = tl.miles_driven !== "" ? Number(tl.miles_driven) : null;
        if (cf.break_minutes) body.break_minutes = tl.break_minutes !== "" ? Number(tl.break_minutes) : null;
        if (cf.extra_cost) body.extra_cost = tl.extra_cost !== "" ? Number(tl.extra_cost) : null;

	  const shouldRecalcVisitRoute =
	    !telehealth &&
	    (cf.drive_end_time || cf.drive_start_time || cf.miles_driven); // ✅ miles triggers ORS

	  if (shouldRecalcVisitRoute) {
	    const fromP = prevPhysical?.point || null;
	    const toP = getVisitRoutePoint(v);

	    if (fromP && toP) {
	      body.recalc_route = 1;
	      body.route_from_lat = fromP.lat;
	      body.route_from_lon = fromP.lon;
	      body.route_to_lat = toP.lat;
	      body.route_to_lon = toP.lon;
	    }
	  }
        

        await API.put(`/time-log/${v.id}`, body);
      }

      // save work
      for (const w of (workBlocks || [])) {
        const workId = Number(w.work_id || w.id);
        const key = `w_${workId}`;
        if (!changed[key]) continue;

        const tl = w.work_time_log || {};
        const isRemote = String(w.location_mode || "") === "remote";
        const isCanceled = String(w.status || "") === "canceled";

        const cf = changedFields[key] || {};
        const body = {};

        if (!isCanceled) {
          if (cf.subjective_in_time) body.subjective_in_time = tl.subjective_in_time || null;
          if (cf.subjective_out_time) body.subjective_out_time = tl.subjective_out_time || null;

          if (cf.drive_start_time) body.drive_start_time = tl.drive_start_time || null;
          if (cf.drive_end_time) body.drive_end_time = tl.drive_end_time || null;

          if (cf.miles_driven) body.miles_driven = tl.miles_driven !== "" ? Number(tl.miles_driven) : null;
          if (cf.break_minutes) body.break_minutes = tl.break_minutes !== "" ? Number(tl.break_minutes) : null;
          if (cf.extra_cost) body.extra_cost = tl.extra_cost !== "" ? Number(tl.extra_cost) : null;

		const shouldRecalcWorkRoute =
		  !isCanceled &&
		  !isRemote &&
		  (cf.drive_end_time || cf.drive_start_time || cf.miles_driven); // ✅ miles triggers ORS

		if (shouldRecalcWorkRoute) {
		  const prevPhysical2 = findPrevPhysicalForKey(key);
		  const fromP = prevPhysical2?.point || null;
		  const toP = getWorkRoutePoint(w);

		  if (fromP && toP) {
		    body.recalc_route = 1;
		    body.route_from_lat = fromP.lat;
		    body.route_from_lon = fromP.lon;
		    body.route_to_lat = toP.lat;
		    body.route_to_lon = toP.lon;
		  }
		}
          
        }

        await API.put(`/work-time-log/${workId}`, body);
      }

      // refresh
      const res = await API.get("/time-log/my", { params: { date: serverDate } });
      setVisits(res.data.visits || []);

      try {
        const wRes = await API.get("/teacher-work/range", {
          params: { date_from: serverDate, date_to: serverDate },
        });
        setWorkBlocks(normalizeTeacherWorkToUnified(wRes.data || []));
      } catch {
        setWorkBlocks([]);
      }

      setChanged({});
      setChangedFields({});
      alert("Saved successfully!");
    } catch (err) {
      console.error(err);
      alert("Failed to save!");
    }
    setSaving(false);
  };

  if (loading) return <div className="p-6 text-center text-gray-400">Loading...</div>;

  return (
    <div className="max-w-2xl mx-auto pb-32 pt-5 px-2">
      <h2 className="text-lg font-bold mb-5">Today's Time Log</h2>

      {unified.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-52 text-gray-400 text-lg">
          <span>No visits/work for today.</span>
        </div>
      ) : (
        <div>
          {unified.map((it, uIdx) => {
            // ---------------- WORK ----------------
            if (isWorkItem(it)) {
              const w = it;
              const workId = Number(w.work_id || w.id);
              const key = `w_${workId}`;
              const tl = w.work_time_log || {};

              const isCanceled = String(w.status || "") === "canceled";
              const isRemote = String(w.location_mode || "") === "remote";
              const canRoute = !isCanceled && !isRemote;

              const prevPhysical = findPrevPhysicalForKey(key);
              const showInterSlot = canRoute && Boolean(prevPhysical);

              const locLabel =
                w.location_mode === "org_location"
                  ? (w.org_location_name || "Org location")
                  : w.location_mode === "teacher_home"
                  ? "Teacher home"
                  : "Remote";

              const dIn = !isCanceled && tl.subjective_in_time ? diffMin(tl.subjective_in_time, w.checkin_time) : null;
              const dOut = !isCanceled && tl.subjective_out_time ? diffMin(tl.subjective_out_time, w.checkout_time) : null;

              return (
                <Fragment key={`work_${workId}_${uIdx}`}>
                  {showInterSlot && (
                    <div className="rounded-2xl border bg-gray-50 p-3 mb-4">
                      <div className="text-xs tracking-wide text-gray-500 mb-2">
                        Drive & break from{" "}
                        <span className="font-semibold">
                          {prevPhysical
                            ? prevPhysical.kind === "work"
                              ? workFromLabel(prevPhysical.item)
                              : `${prevPhysical.item.student_first_name} ${prevPhysical.item.student_last_name}`
                            : "home"}
                        </span>{" "}
                        →{" "}
                        <span className="font-semibold">{`work (${String(w.work_type || "other")} @ ${locLabel})`}</span>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-[11px] text-gray-500 mb-1">Drive Start</label>
                          <input
                            type="time"
                            lang="en-GB"
                            step="60"
                            className="w-full border rounded-lg px-3 py-2"
                            value={hmFromDateTime(tl.drive_start_time)}
                            onChange={(e) => updateWorkTimeHM(w, "drive_start_time", e.target.value)}
                          />
                        </div>
                        <div>
                          <label className="block text-[11px] text-gray-500 mb-1">Drive End</label>
                          <input
                            type="time"
                            lang="en-GB"
                            step="60"
                            className="w-full border rounded-lg px-3 py-2"
                            value={hmFromDateTime(tl.drive_end_time)}
                            onChange={(e) => updateWorkTimeHM(w, "drive_end_time", e.target.value)}
                          />
                        </div>

                        <div>
                          <label className="block text-[11px] text-gray-500 mb-1">Miles (teacher)</label>
                          <input
                            type="number"
                            step="0.1"
                            min="0"
                            className="w-full border rounded-lg px-3 py-2"
                            value={tl.miles_driven ?? ""}
                            onChange={(e) => updateWorkTLField(workId, "miles_driven", e.target.value)}
                          />
                        </div>

                        <div>
                          <label className="block text-[11px] text-gray-500 mb-1">Paid Break (min)</label>
                          <input
                            type="number"
                            min="0"
                            className="w-full border rounded-lg px-3 py-2"
                            value={tl.break_minutes ?? ""}
                            onChange={(e) => updateWorkTLField(workId, "break_minutes", e.target.value)}
                          />
                        </div>

                        <div>
                          <label className="block text-[11px] text-gray-500 mb-1">Extra cost ($)</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            className="w-full border rounded-lg px-3 py-2"
                            value={tl.extra_cost ?? ""}
                            onChange={(e) => updateWorkTLField(workId, "extra_cost", e.target.value)}
                          />
                        </div>

                        <div className="col-span-2">
                          <div className="text-[11px] text-gray-500 mb-1">Route (objective, Google/ORS)</div>
                          <div className="font-mono text-sm">
                            {tl.objective_route_distance_km != null || tl.objective_route_duration_min != null
                              ? (() => {
                                  const km = tl.objective_route_distance_km != null ? Number(tl.objective_route_distance_km) : null;
                                  const mi = km != null ? kmToMiles(km) : null;
                                  const mins = tl.objective_route_duration_min ?? "?";
                                  return `${mi ?? "?"} mi • ${mins} min`;
                                })()
                              : "-"}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div
                    className={`rounded-2xl shadow p-4 mb-6 ${
                      isCanceled ? "bg-purple-50" : isRemote ? "bg-slate-50 border border-slate-200" : "bg-sky-50"
                    }`}
                  >
                    <div className="flex justify-between items-center mb-2 gap-2">
                      <div className="flex flex-col">
                        <div className="font-semibold text-base flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-slate-600" />
                          <span>{`Work: ${String(w.work_type || "other")}`}</span>
                          {isRemote && (
                            <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-slate-100 border border-slate-200 text-slate-700">
                              <Globe size={12} />
                              Remote
                            </span>
                          )}
                        </div>

                        <div className="mt-1 text-xs text-slate-600">
                          <span className="font-semibold">Location:</span> {locLabel}
                        </div>

                        {isCanceled && w.canceled_reason && (
                          <div className="mt-1 text-xs italic text-purple-700">{w.canceled_reason}</div>
                        )}
                      </div>

                      <div className="flex items-center gap-2 flex-wrap justify-end">
                        <div className="font-mono text-lg">{fmtWorkRange(w)}</div>
                        {isCanceled && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 text-xs font-semibold tracking-wide">
                            Canceled
                          </span>
                        )}
                      </div>
                    </div>

                    {!isCanceled && (
                      <>
                        <div className="grid grid-cols-2 gap-4 mt-3">
                          <div>
                            <div className="text-[11px] text-gray-500">Check-in</div>
                            <div className="font-mono">{fmtHMS((w.checkin_time || "").split(" ")[1] || "")}</div>
                          </div>
                          <div>
                            <div className="text-[11px] text-gray-500">Check-out</div>
                            <div className="font-mono">{fmtHMS((w.checkout_time || "").split(" ")[1] || "")}</div>
                          </div>

                          <div>
                            <label className="block text-[11px] text-gray-500 mb-1">Time-in (teacher)</label>
                            <input
                              type="time"
                              lang="en-GB"
                              step="60"
                              className="w-full border rounded-lg px-3 py-2"
                              value={hmFromDateTime(tl.subjective_in_time)}
                              onChange={(e) => updateWorkTimeHM(w, "subjective_in_time", e.target.value)}
                            />
                          </div>
                          <div>
                            <label className="block text-[11px] text-gray-500 mb-1">Time-out (teacher)</label>
                            <input
                              type="time"
                              lang="en-GB"
                              step="60"
                              className="w-full border rounded-lg px-3 py-2"
                              value={hmFromDateTime(tl.subjective_out_time)}
                              onChange={(e) => updateWorkTimeHM(w, "subjective_out_time", e.target.value)}
                            />
                          </div>
                        </div>

                        <div className="mt-2 text-sm">
                          <span className="text-gray-500 mr-2">Δ in:</span>
                          {deltaBadge(dIn)}
                          <span className="text-gray-500 mx-2">•</span>
                          <span className="text-gray-500 mr-2">Δ out:</span>
                          {deltaBadge(dOut)}
                        </div>
                      </>
                    )}
                  </div>
                </Fragment>
              );
            }

            // ---------------- VISIT ----------------
            const v = it;
            const tl = v.time_log || {};
            const telehealth = isTelehealthVisit(v);

            const key = `v_${v.id}`;
            const prevPhysical = findPrevPhysicalForKey(key);

            const isEvent = v.type === "event";
            const isPI = !isEvent && v.service_type === "PI";
            const isCanceled = v.status === "canceled";
            const isSpecialNoShow = isCanceled && isSpecialCanceled(v);

            const allowInterSlot = (!isCanceled || isSpecialNoShow) && !telehealth;
            const showInterSlot = allowInterSlot && Boolean(prevPhysical);

            // ✅ show fields for ALL non-canceled + for NoShow canceled only
            const showVisitFields = !isCanceled || isSpecialNoShow;

            // ✅ NoShow stays editable; normal canceled gets hidden anyway
            const visitInputsDisabled = isCanceled && !isSpecialNoShow;

            const dIn = showVisitFields && tl.subjective_in_time ? diffMin(tl.subjective_in_time, v.checkin_time) : null;
            const dOut = showVisitFields && tl.subjective_out_time ? diffMin(tl.subjective_out_time, v.checkout_time) : null;

            const atClinic = String(v.location_mode || "").toLowerCase() === "at_clinic";
            const locationBadge = atClinic ? (
              <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold bg-slate-100 border border-slate-200 text-slate-700">
                <Building2 size={12} />
                Clinic
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold bg-slate-100 border border-slate-200 text-slate-700">
                <Home size={12} />
                Home
              </span>
            );

            return (
              <Fragment key={v.id}>
                {showInterSlot && (
                  <div className="rounded-2xl border bg-gray-50 p-3 mb-4">
                    <div className="text-xs tracking-wide text-gray-500 mb-2">
                      Drive & break from{" "}
                      <span className="font-semibold">
                        {prevPhysical
                          ? prevPhysical.kind === "work"
                            ? workFromLabel(prevPhysical.item)
                            : `${prevPhysical.item.student_first_name} ${prevPhysical.item.student_last_name}`
                          : "home"}
                      </span>{" "}
                      →{" "}
                      <span className="font-semibold">{v.student_first_name} {v.student_last_name}</span>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[11px] text-gray-500 mb-1">Drive Start</label>
                        <input
                          type="time"
                          lang="en-GB"
                          step="60"
                          className="w-full border rounded-lg px-3 py-2"
                          value={hmFromDateTime(tl.drive_start_time)}
                          onChange={(e) => updateVisitTimeHM(v, "drive_start_time", e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] text-gray-500 mb-1">Drive End</label>
                        <input
                          type="time"
                          lang="en-GB"
                          step="60"
                          className="w-full border rounded-lg px-3 py-2"
                          value={hmFromDateTime(tl.drive_end_time)}
                          onChange={(e) => updateVisitTimeHM(v, "drive_end_time", e.target.value)}
                        />
                      </div>

                      <div>
                        <label className="block text-[11px] text-gray-500 mb-1">Miles (teacher)</label>
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          className="w-full border rounded-lg px-3 py-2"
                          value={tl.miles_driven ?? ""}
                          onChange={(e) => updateVisitTLField(v.id, "miles_driven", e.target.value)}
                        />
                      </div>

                      <div>
                        <label className="block text-[11px] text-gray-500 mb-1">Paid Break (min)</label>
                        <input
                          type="number"
                          min="0"
                          className="w-full border rounded-lg px-3 py-2"
                          value={tl.break_minutes ?? ""}
                          onChange={(e) => updateVisitTLField(v.id, "break_minutes", e.target.value)}
                        />
                      </div>

                      <div>
                        <label className="block text-[11px] text-gray-500 mb-1">Extra cost ($)</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          className="w-full border rounded-lg px-3 py-2"
                          value={tl.extra_cost ?? ""}
                          onChange={(e) => updateVisitTLField(v.id, "extra_cost", e.target.value)}
                        />
                      </div>

                      <div className="col-span-2">
                        <div className="text-[11px] text-gray-500 mb-1">Route (objective, Google/ORS)</div>
                        <div className="font-mono text-sm">
                          {tl.objective_route_distance_km != null || tl.objective_route_duration_min != null
                            ? (() => {
                                const km = tl.objective_route_distance_km != null ? Number(tl.objective_route_distance_km) : null;
                                const mi = km != null ? kmToMiles(km) : null;
                                const mins = tl.objective_route_duration_min ?? "?";
                                return `${mi ?? "?"} mi • ${mins} min`;
                              })()
                            : "-"}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div
                  className={`rounded-2xl shadow p-4 mb-6 ${
                    v.status === "canceled"
                      ? "bg-purple-50"
                      : telehealth
                      ? "border-l-4 border-sky-400 bg-sky-50"
                      : isPI
                      ? "border-l-4 border-orange-400 bg-orange-50"
                      : isEvent
                      ? "border-l-4 border-yellow-400 bg-yellow-50"
                      : "bg-green-50"
                  }`}
                >
                  <div className="flex justify-between items-center mb-2 gap-2">
                    <div className="flex flex-col">
                      <div className="font-semibold text-base">{v.student_first_name} {v.student_last_name}</div>

                      <div className="flex items-center gap-2 flex-wrap mt-1">
                        {locationBadge}
                        {atClinic && v.clinic_name ? <span className="text-[11px] text-slate-600">{v.clinic_name}</span> : null}

                        {v.discipline_name && !isEvent ? (
                          <span className="text-[11px] text-gray-600">{v.discipline_name}</span>
                        ) : null}

                        {!isEvent && v.service_type ? <DisciplineBadge serviceType={v.service_type} /> : null}
                        {isEvent ? <EventBadge /> : null}
                        {telehealth ? <TelehealthBadge /> : null}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap justify-end">
                      <div className="font-mono text-lg">{fmtTimeRange(v.scheduled_at, v.event_end_time)}</div>
                      {v.status === "canceled" && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 text-xs font-semibold tracking-wide">
                          Canceled
                        </span>
                      )}
                    </div>
                  </div>

                  {v.status === "canceled" && v.notes ? (
                    <div className="mt-1 text-xs italic text-purple-700">{v.notes}</div>
                  ) : null}

                  {/* ✅ Show fields for ALL non-canceled + NoShow canceled */}
                  {showVisitFields && (
                    <>
                      <div className="grid grid-cols-2 gap-4 mt-3">
                        <div>
                          <div className="text-[11px] text-gray-500">Check-in</div>
                          <div className="font-mono">{fmtHMS((v.checkin_time || "").split(" ")[1] || "")}</div>
                        </div>
                        <div>
                          <div className="text-[11px] text-gray-500">Check-out</div>
                          <div className="font-mono">{fmtHMS((v.checkout_time || "").split(" ")[1] || "")}</div>
                        </div>

                        <div>
                          <label className="block text-[11px] text-gray-500 mb-1">Time-in (teacher)</label>
                          <input
                            type="time"
                            lang="en-GB"
                            step="60"
                            className="w-full border rounded-lg px-3 py-2"
                            value={hmFromDateTime(tl.subjective_in_time)}
                            onChange={(e) => updateVisitTimeHM(v, "subjective_in_time", e.target.value)}
                            disabled={visitInputsDisabled}
                          />
                        </div>
                        <div>
                          <label className="block text-[11px] text-gray-500 mb-1">Time-out (teacher)</label>
                          <input
                            type="time"
                            lang="en-GB"
                            step="60"
                            className="w-full border rounded-lg px-3 py-2"
                            value={hmFromDateTime(tl.subjective_out_time)}
                            onChange={(e) => updateVisitTimeHM(v, "subjective_out_time", e.target.value)}
                            disabled={visitInputsDisabled}
                          />
                        </div>
                      </div>

                      <div className="mt-2 text-sm">
                        <span className="text-gray-500 mr-2">Δ in:</span>
                        {deltaBadge(dIn)}
                        <span className="text-gray-500 mx-2">•</span>
                        <span className="text-gray-500 mr-2">Δ out:</span>
                        {deltaBadge(dOut)}
                      </div>
                    </>
                  )}
                </div>
              </Fragment>
            );
          })}
        </div>
      )}

      <div className="fixed bottom-0 left-0 w-full flex justify-center pb-5 z-30 bg-gradient-to-t from-white via-white/80 to-transparent">
        <button
          className="bg-blue-700 text-white px-8 py-3 rounded-full shadow-xl text-lg font-bold hover:bg-blue-900 transition-all disabled:opacity-50"
          onClick={handleSave}
          disabled={saving || Object.keys(changed).length === 0}
        >
          {saving ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  );
}








